import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import { DESKTOP_MIN_WIDTH, EDIT_RECEIVER_SUBURB_NAME } from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';

const CITIES_SEARCH_NAME = 'cities-search-field';

const CustomCitiesDropdown = ({
  ref,
  field,
  isConfigRequired,
  cities,
  information,
  setInformation,
  validation,
  setValidation,
}) => {
  const { t } = useLang();

  const [searchValue, setSearchValue] = useState('');
  const [citiesSearch, setCitiesSearch] = useState(null);
  const [currentCitySelected, setCurrentCitySelected] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const isSearchInvalid = !searchTextValid(searchValue);

  useInputFocus(CITIES_SEARCH_NAME);

  useEffect(() => {
    if (isEmpty(cities)) return;
    if (cities) {
      const city = cities?.find(
        (c) =>
          c?.name &&
          information[field.name] &&
          c?.name?.trim() === information[field.name]?.name?.trim()
      );

      if (city) {
        setCurrentCitySelected(city);

        const newInformation = {
          ...information,
          [field.name]: city,
        };
        setInformation(newInformation);
      }
    }
  }, [cities]);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setCitiesSearch(null);

      return;
    }

    const newcitySearch = cities?.filter(
      (city) =>
        city?.name?.toLowerCase()?.includes(value?.toLowerCase()) && city
    );
    setCitiesSearch(newcitySearch);
  };

  const handleCitySelected = (city) => {
    setCurrentCitySelected(city);

    setSearchValue('');

    setCitiesSearch(null);

    setShowDropdown(false);

    const newInformation = {
      ...information,
      [field.name]: city,
      [EDIT_RECEIVER_SUBURB_NAME]: '',
    };
    setInformation(newInformation);

    const newValidation = {
      ...validation,
      [field.name]: '',
      [EDIT_RECEIVER_SUBURB_NAME]: '',
    };
    setValidation(newValidation);
  };

  const handleDropdownOnToggle = () => {
    setSearchValue('');
    setCitiesSearch(null);
    setShowDropdown(!isShowDropdown);
  };

  const renderDropdownMenuChildren = () => (
    <>
      <DropdownSearchInput
        ref={ref}
        name={CITIES_SEARCH_NAME}
        value={searchValue || ''}
        placeholder={t('form_placeholder_type_to_search')}
        onChange={handleSearch}
      />
      <DropdownMenuContainer
        style={{ borderRadius: '0 0 8px 8px', paddingBottom: 0 }}
      >
        {renderDropdownItems(isSearchInvalid ? [] : citiesSearch || cities)}
      </DropdownMenuContainer>
    </>
  );
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
        >
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoItemText>{t('form_text_no_result')}</NoItemText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={item.id}
            eventKey={index}
            onClick={() => handleCitySelected(item)}
          >
            {item.name}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  return (
    <CityDropdownStyled>
      <Label>
        {t(field.label)}
        {(isConfigRequired ?? field.isRequired) && <span>*</span>}
      </Label>
      <DropdownCommon
        value={currentCitySelected?.name}
        placeholder={field.placeholder}
        keyError={validation[field.name]}
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
      />
    </CityDropdownStyled>
  );
};

const CityDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  margin-bottom: 8px;

  & span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const SearchErrorText = styled(NoItemText)`
  color: var(--ds-c-red);
`;

export default CustomCitiesDropdown;
